import React from 'react';
import { Stack, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { RHFUploadSingleFile } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import { UseFormSetValue, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';

interface FormValuesProps {
  videoFile: File | null;
  videoName: string;
}

interface UploadVideoProps {
  setValue: UseFormSetValue<FormValuesProps>;
  handleCloseModal: () => void;
}

export const UploadVideo: React.FC<UploadVideoProps> = ({ setValue, handleCloseModal }) => {
  const { enqueueSnackbar } = useSnackbar();

  const videoName = useWatch({ name: 'videoName'});

  const handleFileChange = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isMov = file.type === 'video/quicktime';

      if (isMov && isSafari) {
        enqueueSnackbar(
          'Your browser is unable to play the video. Please use Chrome for best experience.',
          {
            variant: 'error',
          }
        );
      }

      const mediaElement = document.createElement('video');
      mediaElement.src = URL.createObjectURL(file);

      mediaElement.addEventListener('loadedmetadata', () => {
        const duration = mediaElement.duration;

        if (duration > 1800) {
          enqueueSnackbar('Video duration exceeds the 30-minute limit.', { variant: 'error' });
          setValue('videoFile', null);
        } else {
          setValue(
            'videoFile',
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
        }

        // Clean up the media element
        mediaElement.remove();
      });
    }
  };

  return (
    <Stack>
      <Stack gap={3}>
        <RHFUploadSingleFile
          name="videoFile"
          accept={{ 'video/mp4': [], 'video/quicktime': ['.mov'] }}
          maxSize={2147483648} // 2GB limit
          setValue={setValue}
          handleDrop={handleFileChange} // Custom handler for file drop
        />

        <TextField
          name="videoName"
          fullWidth
          value={videoName}
          onChange={(e) => setValue('videoName', e.target.value)}
          placeholder="Enter Video Name"
        />

        <LoadingButton type="submit" variant="contained" fullWidth sx={styles.submitBtn}>
          Submit
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

const styles: Styles = {
  submitBtn: {
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
    py: 2.5,
  },
};
